import React, { useState, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  Divider,
  Button,
  Chip,
  Stack,
  Card,
  CardContent,
  TextField,
  Alert,
  CircularProgress,
  useTheme,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Tabs,
  Tab,
  Checkbox,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Badge,
  Rating,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as AcceptIcon,
  Cancel as RejectIcon,
  Visibility as ViewIcon,
  Person as PersonIcon,
  People as PeopleIcon,
  Store as StoreIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Star as StarIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  Link as LinkIcon,
  Description as DescriptionIcon,
  AttachMoney as MoneyIcon,
  InsertChart as AnalyticsIcon,
  EventSeat as EventSeatIcon,
  Sort as SortIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  FileDownload as FileDownloadIcon,
  TableChart as TableChartIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  MoreVert as MoreVertIcon,
  HourglassEmpty as PendingIcon,
  CalendarToday as CalendarIcon,
  TrendingUp as TrendingUpIcon,
  Percent as PercentIcon,
  AccountBalanceWallet as WalletIcon,
  Speed as SpeedIcon,
  Assessment as AssessmentIcon,
  Quiz as QuizIcon,
  Send as SendIcon,
  Message as MessageIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, LineChart, Line, AreaChart, Area } from 'recharts';
import { getEventById, getEventApplications, updateApplicationStatus, updateApplicationAssignment, confirmPayment, sendMassEmail } from '../services/eventService';
import { createOrGetConversation } from '../services/messagingService';
import { getImageUrl, getVendorPlaceholderImage } from '../utils/imageUtils';
import { apiWithAuth } from '../services/api';

const EventCRM = () => {
  const theme = useTheme();
  const { eventId } = useParams();
  const history = useHistory();
  const queryClient = useQueryClient();
  
  console.log('EventCRM: Rendering for eventId:', eventId);
  
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date'); // date, rating, name, category
  const [sortOrder, setSortOrder] = useState('desc'); // asc, desc
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [selectedAppForStatusChange, setSelectedAppForStatusChange] = useState(null);
  const [selectedApplications, setSelectedApplications] = useState([]);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [questionnaireDialogOpen, setQuestionnaireDialogOpen] = useState(false);
  const [selectedAppForQuestionnaire, setSelectedAppForQuestionnaire] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [editingAssignment, setEditingAssignment] = useState(null); // { applicationId, field: 'table' or 'setup' or 'tier' }
  const [editValues, setEditValues] = useState({});
  const [capacityError, setCapacityError] = useState('');
  const [massEmailDialogOpen, setMassEmailDialogOpen] = useState(false);
  const [massEmailData, setMassEmailData] = useState({
    subject: '',
    message: '',
    recipient_filter: 'all'
  });
  const [sendingEmail, setSendingEmail] = useState(false);

  // Fetch event details
  const { data: event, isLoading: eventLoading, error: eventError } = useQuery(
    ['event', eventId],
    () => getEventById(eventId)
  );

  // Fetch event applications
  const { data: applications, isLoading: applicationsLoading, error: applicationsError } = useQuery(
    ['eventApplications', eventId],
    () => getEventApplications(eventId),
    {
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      staleTime: 0, // Always consider data stale to ensure fresh data after mutations
      onSuccess: (data) => {
        console.log('EventCRM: Applications loaded:', data);
        // Parse social_media if it's a string
        if (data && Array.isArray(data)) {
          data.forEach(app => {
            if (app.vendor && typeof app.vendor.social_media === 'string') {
              try {
                app.vendor.social_media = JSON.parse(app.vendor.social_media);
              } catch (e) {
                app.vendor.social_media = null;
              }
            }
          });
        }
      },
      onError: (error) => {
        console.error('EventCRM: Error loading applications:', error);
      }
    }
  );

  // Update application status mutation
  const updateStatusMutation = useMutation(
    ({ applicationId, status }) => updateApplicationStatus(applicationId, status),
    {
      onSuccess: () => {
        // Invalidate all related queries
        queryClient.invalidateQueries(['eventApplications', eventId]);
        queryClient.invalidateQueries(['eventStats', eventId]);
        queryClient.invalidateQueries('applications');
        queryClient.invalidateQueries('vendorApplications');
        queryClient.invalidateQueries('notifications');
        queryClient.invalidateQueries('unreadNotificationCount');
        queryClient.invalidateQueries(['event', eventId]);
        queryClient.invalidateQueries(['eventStats']);
        
        // Force immediate refetch to update UI instantly
        queryClient.refetchQueries(['eventApplications', eventId]);
        queryClient.refetchQueries(['eventStats', eventId]);
        queryClient.refetchQueries(['event', eventId]);
        
        setSelectedApplications([]);
        setCapacityError(''); // Clear any capacity errors
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.detail || error.message || 'Failed to update application status';
        if (errorMessage.includes('capacity') || errorMessage.includes('full')) {
          setCapacityError(errorMessage);
          setTimeout(() => setCapacityError(''), 5000);
        }
      },
    }
  );

  // Confirm payment mutation
  const confirmPaymentMutation = useMutation(
    (applicationId) => confirmPayment(applicationId),
    {
      onSuccess: (data) => {
        console.log('Payment confirmed, response:', data);
        // Force refetch by invalidating and then refetching
        queryClient.invalidateQueries(['eventApplications', eventId]);
        queryClient.invalidateQueries(['eventStats', eventId]);
        queryClient.invalidateQueries(['event', eventId]);
        queryClient.invalidateQueries('applications');
        queryClient.invalidateQueries('notifications');
        queryClient.invalidateQueries('unreadNotificationCount');
        // Refetch immediately to ensure UI updates
        queryClient.refetchQueries(['eventApplications', eventId]);
        queryClient.refetchQueries(['eventStats', eventId]);
        // Also invalidate globally to update event detail pages that might be open
        queryClient.invalidateQueries(['eventStats']);
        setCapacityError(''); // Clear any capacity errors
      },
      onError: (error) => {
        console.error('Error confirming payment:', error);
        const errorMessage = error.response?.data?.detail || error.message || 'Failed to confirm payment';
        if (errorMessage.includes('capacity') || errorMessage.includes('full')) {
          setCapacityError(errorMessage);
          setTimeout(() => setCapacityError(''), 5000);
        }
      },
    }
  );

  // Update assignment mutation
  const updateAssignmentMutation = useMutation(
    ({ applicationId, tableId, setupId, selectedTierId }) => updateApplicationAssignment(applicationId, tableId, setupId, selectedTierId),
    {
      onSuccess: (updatedApplication) => {
        // Optimistically update the cache
        queryClient.setQueryData(['eventApplications', eventId], (oldData) => {
          if (!oldData) return oldData;
          return oldData.map(app => 
            app.application_id === updatedApplication.application_id 
              ? updatedApplication 
              : app
          );
        });
        // Also refetch to ensure consistency with server
        queryClient.refetchQueries(['eventApplications', eventId]);
        setEditingAssignment(null);
        setEditValues({});
      },
      onError: (error) => {
        console.error('updateAssignmentMutation - Error:', error);
        // If update fails, refetch to ensure consistency
        queryClient.invalidateQueries(['eventApplications', eventId]);
        queryClient.refetchQueries(['eventApplications', eventId]);
      },
    }
  );

  // Bulk update mutation
  const bulkUpdateMutation = useMutation(
    async ({ applicationIds, status }) => {
      const promises = applicationIds.map(id => updateApplicationStatus(id, status));
      return Promise.all(promises);
    },
    {
      onSuccess: () => {
        // Invalidate all related queries
        queryClient.invalidateQueries(['eventApplications', eventId]);
        queryClient.invalidateQueries(['eventStats', eventId]);
        queryClient.invalidateQueries(['event', eventId]);
        queryClient.invalidateQueries('applications');
        queryClient.invalidateQueries('vendorApplications');
        queryClient.invalidateQueries('notifications');
        queryClient.invalidateQueries('unreadNotificationCount');
        
        // Force immediate refetch to update UI instantly
        queryClient.refetchQueries(['eventApplications', eventId]);
        queryClient.refetchQueries(['eventStats', eventId]);
        queryClient.refetchQueries(['event', eventId]);
        
        setSelectedApplications([]);
        setCapacityError(''); // Clear any capacity errors
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.detail || error.message || 'Failed to update application status';
        if (errorMessage.includes('capacity') || errorMessage.includes('full')) {
          setCapacityError(errorMessage);
          setTimeout(() => setCapacityError(''), 5000);
        }
      },
    }
  );

  // Filter and search applications
  const filteredApplications = useMemo(() => {
    if (!applications) return [];
    
    let filtered = applications;
    
    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(app => 
        app.status?.toLowerCase() === statusFilter.toLowerCase()
      );
    }
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(app => 
        app.vendor?.business_name?.toLowerCase().includes(query) ||
        app.vendor?.contact_info?.toLowerCase().includes(query) ||
        app.vendor?.bio?.toLowerCase().includes(query) ||
        app.comments?.toLowerCase().includes(query)
      );
    }
    
    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(app => {
        const vendorCategory = app.vendor?.category_name || 'Uncategorized';
        return vendorCategory === categoryFilter;
      });
    }
    
    // Tab filter
    if (activeTab === 1) { // Pending
      filtered = filtered.filter(app => {
        // Exclude waitlisted applications from pending tab
        const statusLower = app.status?.toLowerCase();
        return statusLower === 'pending' && 
               statusLower !== 'waitlist' &&
               (app.waitlist_position === null || app.waitlist_position === undefined);
      });
    } else if (activeTab === 2) { // Payment Pending
      filtered = filtered.filter(app => 
        (app.status?.toLowerCase() === 'accepted' || app.status?.toLowerCase() === 'approved') &&
        app.payment_status !== 'confirmed'
      );
    } else if (activeTab === 3) { // Confirmed
      filtered = filtered.filter(app => {
        const statusLower = app.status?.toLowerCase();
        const paymentStatus = app.payment_status?.toLowerCase();
        
        // Include if status is confirmed (or confirm-payment)
        // Payment status should also be confirmed, but if status is confirmed, include it
        const isStatusConfirmed = (statusLower === 'confirmed' || statusLower === 'confirm-payment');
        const isPaymentConfirmed = (paymentStatus === 'confirmed');
        
        // Debug logging
        if (isStatusConfirmed && !isPaymentConfirmed) {
          console.log('Confirmed status but payment_status not confirmed:', {
            application_id: app.application_id,
            status: app.status,
            payment_status: app.payment_status,
            paymentStatus_lower: paymentStatus
          });
        }
        
        // If status is confirmed, include it regardless of payment_status
        // (payment_status should be confirmed, but if it's missing or different, still show it)
        return isStatusConfirmed;
      });
    } else if (activeTab === 4) { // Rejected
      filtered = filtered.filter(app => app.status?.toLowerCase() === 'rejected');
    } else if (activeTab === 5) { // Waitlist
      filtered = filtered.filter(app => 
        app.status?.toLowerCase() === 'waitlist' || 
        (app.waitlist_position !== null && app.waitlist_position !== undefined)
      );
    }
    
    // Sorting
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch(sortBy) {
        case 'name':
          const nameA = a.vendor?.business_name || '';
          const nameB = b.vendor?.business_name || '';
          comparison = nameA.localeCompare(nameB);
          break;
        case 'category':
          const catA = a.vendor?.category_name || 'Uncategorized';
          const catB = b.vendor?.category_name || 'Uncategorized';
          comparison = catA.localeCompare(catB);
          break;
        case 'date':
          comparison = new Date(a.created_at) - new Date(b.created_at);
          break;
        case 'rating':
          // TODO: Will need to fetch ratings - for now use 0
          comparison = 0;
          break;
        default:
          comparison = new Date(b.created_at) - new Date(a.created_at);
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    
    return filtered;
  }, [applications, statusFilter, searchQuery, activeTab, categoryFilter, sortBy, sortOrder]);

  // Get unique categories for filter dropdown
  const availableCategories = useMemo(() => {
    if (!applications) return [];
    const categories = new Set();
    applications.forEach(app => {
      const cat = app.vendor?.category_name || 'Uncategorized';
      if (cat) categories.add(cat);
    });
    return Array.from(categories).sort();
  }, [applications]);

  // Stats
  const stats = useMemo(() => {
    if (!applications) return { total: 0, pending: 0, accepted: 0, confirmed: 0, paymentPending: 0, rejected: 0, waitlist: 0, filled: 0, available: 0 };
    
    const confirmed = applications.filter(app => {
      const statusLower = app.status?.toLowerCase();
      // Match the filter logic - count if status is confirmed (or confirm-payment)
      return (statusLower === 'confirmed' || statusLower === 'confirm-payment');
    }).length;
    
    const paymentPending = applications.filter(app => 
      (app.status?.toLowerCase() === 'accepted' || app.status?.toLowerCase() === 'approved') &&
      app.payment_status !== 'confirmed'
    ).length;
    
    const waitlistCount = applications.filter(app => 
      app.status?.toLowerCase() === 'waitlist' ||
      (app.waitlist_position !== null && app.waitlist_position !== undefined)
    ).length;
    
    const eventCapacity = event?.capacity || 0;
    
    const total = applications.length;
    const pending = applications.filter(app => {
      const statusLower = app.status?.toLowerCase();
      return statusLower === 'pending' && 
             statusLower !== 'waitlist' &&
             (app.waitlist_position === null || app.waitlist_position === undefined);
    }).length;
    const rejected = applications.filter(app => app.status?.toLowerCase() === 'rejected').length;
    const accepted = paymentPending;
    const totalReviewed = total - pending;
    
    // Calculate rates
    const acceptanceRate = totalReviewed > 0 ? ((accepted + confirmed) / totalReviewed * 100) : 0;
    const conversionRate = total > 0 ? (confirmed / total * 100) : 0;
    const paymentCompletionRate = (accepted + confirmed) > 0 ? (confirmed / (accepted + confirmed) * 100) : 0;
    const capacityUtilization = eventCapacity > 0 ? (confirmed / eventCapacity * 100) : 0;
    
    return {
      total,
      pending,
      accepted,
      confirmed,
      paymentPending,
      waitlist: waitlistCount,
      rejected,
      filled: confirmed,
      available: eventCapacity > 0 ? Math.max(0, eventCapacity - confirmed) : null,
      capacity: eventCapacity,
      acceptanceRate,
      conversionRate,
      paymentCompletionRate,
      capacityUtilization,
    };
  }, [applications, event]);

  // Category Statistics
  const categoryStats = useMemo(() => {
    if (!applications) return {};
    
    const categoryData = {};
    applications.forEach(app => {
      const categoryName = app.vendor?.category_name || 'Uncategorized';
      if (!categoryData[categoryName]) {
        categoryData[categoryName] = { total: 0, pending: 0, accepted: 0, rejected: 0 };
      }
      categoryData[categoryName].total++;
      const status = app.status?.toLowerCase();
      if (status === 'pending') {
        categoryData[categoryName].pending++;
      } else if (status === 'accepted') {
        categoryData[categoryName].accepted++;
      } else if (status === 'rejected') {
        categoryData[categoryName].rejected++;
      }
    });
    
    return categoryData;
  }, [applications]);

  // Tier Statistics
  const tierStats = useMemo(() => {
    if (!event) return {};
    
    const tierData = {};
    
    // If there are tiers, initialize them
    if (event?.table_tiers && event.table_tiers.length > 0) {
      event.table_tiers.forEach(tier => {
        tierData[tier.id] = {
          id: tier.id,
          name: tier.name,
          fee: tier.fee,
          description: tier.description,
          capacity: tier.capacity || null,  // Add capacity field
          total: 0,
          pending: 0,
          accepted: 0,
          confirmed: 0,
          paymentPending: 0,
          rejected: 0,
          waitlist: 0,
        };
      });
    } else {
      // No tiers - create a single "default" tier for all applications
      tierData['default'] = {
        id: 'default',
        name: 'Standard Fee',
        fee: event?.fee || 0,
        description: null,
        total: 0,
        pending: 0,
        accepted: 0,
        confirmed: 0,
        paymentPending: 0,
        rejected: 0,
        waitlist: 0,
      };
    }
    
    // Only count applications if they exist
    if (!applications || applications.length === 0) {
      return tierData;
    }
    
    // Count applications by tier
    applications.forEach(app => {
      let tierId = null;
      
      if (event?.table_tiers && event.table_tiers.length > 0) {
        // Has tiers - find the tier for this application
        if (app.selected_tier_id !== null && app.selected_tier_id !== undefined) {
          tierId = event.table_tiers.find(tier => 
            tier.id === app.selected_tier_id || 
            tier.id === Number(app.selected_tier_id) ||
            String(tier.id) === String(app.selected_tier_id)
          )?.id;
        }
      } else {
        // No tiers - use default tier for all applications
        tierId = 'default';
      }
      
      if (tierId && tierData[tierId]) {
        tierData[tierId].total++;
        const status = app.status?.toLowerCase();
        
        if (status === 'pending') {
          tierData[tierId].pending++;
        } else if (status === 'accepted' || status === 'approved') {
          tierData[tierId].accepted++;
          if (app.payment_status === 'confirmed') {
            tierData[tierId].confirmed++;
          } else {
            tierData[tierId].paymentPending++;
          }
        } else if (status === 'confirmed' || status === 'confirm-payment') {
          tierData[tierId].confirmed++;
        } else if (status === 'rejected') {
          tierData[tierId].rejected++;
        } else if (status === 'waitlist' || app.waitlist_position !== null) {
          tierData[tierId].waitlist++;
        }
      }
    });
    
    return tierData;
  }, [applications, event]);
  
  // Tier statistics for chart
  const tierBarData = useMemo(() => {
    if (!tierStats || Object.keys(tierStats).length === 0) return [];
    
    return Object.values(tierStats)
      .filter(tier => tier.total > 0)
      .map(tier => ({
        name: tier.name.length > 15 ? tier.name.substring(0, 15) + '...' : tier.name,
        fullName: tier.name,
        'Total': tier.total,
        'Confirmed': tier.confirmed,
        'Payment Pending': tier.paymentPending,
        'Pending': tier.pending,
        'Rejected': tier.rejected,
      }))
      .sort((a, b) => b.Total - a.Total);
  }, [tierStats]);

  // Extended color palette for pie charts - ensures each category gets a unique color
  const COLORS = [
    '#0088FE', // Blue
    '#00C49F', // Teal
    '#FFBB28', // Yellow
    '#FF8042', // Orange
    '#8884D8', // Purple
    '#82CA9D', // Green
    '#FFC658', // Amber
    '#FF6B6B', // Coral
    '#4ECDC4', // Turquoise
    '#95E1D3', // Mint
    '#F38181', // Salmon
    '#AA96DA', // Lavender
    '#FCBAD3', // Pink
    '#A8E6CF', // Light Green
    '#FFD93D', // Gold
  ];

  // Chart data for vendor categories (pie chart)
  const categoryPieData = useMemo(() => {
    // Get all unique category names and sort them for consistent color assignment
    const categories = Object.keys(categoryStats).sort();
    return categories.map((name, index) => ({
      name,
      value: categoryStats[name].total,
      color: COLORS[index % COLORS.length], // Assign color based on sorted position
    }));
  }, [categoryStats]);

  // Chart data for status distribution (pie chart)
  const statusPieData = useMemo(() => {
    return [
      { name: 'Pending', value: stats.pending, color: theme.palette.warning.main },
      { name: 'Payment Pending', value: stats.paymentPending, color: '#FFA726' },
      { name: 'Confirmed', value: stats.confirmed, color: theme.palette.success.main },
      { name: 'Waitlist', value: stats.waitlist, color: theme.palette.info.main },
      { name: 'Rejected', value: stats.rejected, color: theme.palette.error.main },
    ].filter(item => item.value > 0);
  }, [stats, theme]);

  // Bar chart data for categories by status
  const categoryBarData = useMemo(() => {
    return Object.entries(categoryStats).map(([name, stats]) => ({
      name: name.length > 10 ? name.substring(0, 10) + '...' : name,
      fullName: name,
      Pending: stats.pending,
      Accepted: stats.accepted,
      Rejected: stats.rejected,
    }));
  }, [categoryStats]);

  // Application timeline (applications by day)
  const timelineData = useMemo(() => {
    if (!applications) return [];
    
    const timeline = {};
    applications.forEach(app => {
      const date = format(new Date(app.created_at), 'MMM dd');
      if (!timeline[date]) {
        timeline[date] = 0;
      }
      timeline[date]++;
    });
    
    return Object.entries(timeline)
      .map(([date, count]) => ({ date, applications: count }))
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(-7); // Last 7 days
  }, [applications]);


  const handleSelectApplication = (applicationId) => {
    setSelectedApplications(prev =>
      prev.includes(applicationId)
        ? prev.filter(id => id !== applicationId)
        : [...prev, applicationId]
    );
  };

  const handleSelectAll = () => {
    if (selectedApplications.length === filteredApplications.length) {
      setSelectedApplications([]);
    } else {
      setSelectedApplications(filteredApplications.map(app => app.application_id));
    }
  };

  const handleBulkAction = (status) => {
    if (selectedApplications.length === 0) return;
    bulkUpdateMutation.mutate({
      applicationIds: selectedApplications,
      status,
    });
  };

  const handleAccept = (applicationId) => {
    // Check capacity before accepting
    const confirmedCount = stats?.confirmed || 0;
    const capacity = event?.capacity;
    
    if (capacity && confirmedCount >= capacity) {
      setCapacityError(`Event capacity (${capacity}) is full. Cannot accept more vendors. Please wait for a confirmed vendor to cancel or reject applications first.`);
      setTimeout(() => setCapacityError(''), 5000);
      return;
    }
    
    setCapacityError(''); // Clear any previous errors
    updateStatusMutation.mutate({ applicationId, status: 'accepted' });
  };

  const handleReject = (applicationId) => {
    updateStatusMutation.mutate({ applicationId, status: 'rejected' });
  };

  const handleStatusMenuOpen = (event, application) => {
    setStatusMenuAnchor(event.currentTarget);
    setSelectedAppForStatusChange(application);
  };

  const handleStatusMenuClose = () => {
    setStatusMenuAnchor(null);
    setSelectedAppForStatusChange(null);
  };

  const handleStatusChange = (newStatus) => {
    if (selectedAppForStatusChange) {
      // Check capacity before accepting
      if ((newStatus.toLowerCase() === 'accepted' || newStatus.toLowerCase() === 'approved')) {
        const confirmedCount = stats?.confirmed || 0;
        const capacity = event?.capacity;
        
        if (capacity && confirmedCount >= capacity) {
          setCapacityError(`Event capacity (${capacity}) is full. Cannot accept more vendors. Please wait for a confirmed vendor to cancel or reject applications first.`);
          setTimeout(() => setCapacityError(''), 5000);
          handleStatusMenuClose();
          return;
        }
      }
      
      // For waitlist status, we can always set it (no capacity check needed)
      setCapacityError(''); // Clear any previous errors
      updateStatusMutation.mutate({ 
        applicationId: selectedAppForStatusChange.application_id, 
        status: newStatus 
      });
    }
    handleStatusMenuClose();
  };

  const handleStartEdit = (applicationId, field, currentValue) => {
    setEditingAssignment({ applicationId, field });
    setEditValues(prev => ({
      ...prev,
      [applicationId]: {
        ...prev[applicationId],
        [field]: currentValue || '',
      },
    }));
  };

  const handleCancelEdit = () => {
    setEditingAssignment(null);
    setEditValues({});
  };

  const handleSaveAssignment = (applicationId) => {
    const application = applications?.find(app => app.application_id === applicationId);
    if (!application) return;
    
    const values = editValues[applicationId] || {};
    const editingField = editingAssignment?.field;
    
    // Only update table_id; setup_id is deprecated
    const tableId = values.table !== undefined 
      ? (values.table === '' ? null : (isNaN(parseInt(values.table)) ? null : parseInt(values.table)))
      : application.table_id;
    
    // Handle tier selection - properly parse the value
    let selectedTierId;
    if (values.tier !== undefined) {
      if (values.tier === '' || values.tier === null) {
        selectedTierId = null;
      } else {
        const parsed = parseInt(values.tier);
        selectedTierId = isNaN(parsed) ? null : parsed;
      }
    } else {
      selectedTierId = application.selected_tier_id;
    }

    // Determine what to send based on what field is being edited
    const updateData = {
      applicationId,
      tableId: editingField === 'table' ? tableId : undefined,
      setupId: undefined, // Don't send setupId
      selectedTierId: editingField === 'tier' ? selectedTierId : undefined,
    };

    updateAssignmentMutation.mutate(updateData);
  };

  const handleEditValueChange = (applicationId, field, value) => {
    setEditValues(prev => ({
      ...prev,
      [applicationId]: {
        ...prev[applicationId],
        [field]: value,
      },
    }));
  };

  const handleViewDetails = (application) => {
    setSelectedApplication(application);
    setDetailDialogOpen(true);
  };

  const handleMessageVendor = async (vendorId, applicationId) => {
    try {
      // Get organizer profile to get organizer_id
      const organizerProfile = await queryClient.fetchQuery(['organizerProfile'], () => {
        const token = localStorage.getItem('token');
        return apiWithAuth(token).get('/organizer/profile').then(res => res.data);
      });
      
      if (!organizerProfile || !organizerProfile.organizer_id) {
        console.error('Organizer profile not found or missing organizer_id:', organizerProfile);
        alert('Organizer profile not found. Please ensure you have created an organizer profile.');
        return;
      }

      console.log('Creating conversation with:', {
        vendorId,
        organizerId: organizerProfile.organizer_id,
        eventId: parseInt(eventId),
        applicationId
      });

      // Create or get conversation
      const result = await createOrGetConversation(
        vendorId,
        organizerProfile.organizer_id,
        parseInt(eventId),
        applicationId
      );

      console.log('Conversation created/retrieved:', result);

      if (!result || !result.conversation_id) {
        throw new Error('Invalid response from server');
      }

      // Navigate to messages page with the conversation
      history.push(`/messages?conversation=${result.conversation_id}`);
    } catch (error) {
      console.error('Error creating conversation:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to start conversation. Please try again.';
      alert(errorMessage);
    }
  };

  const handleViewQuestionnaire = (application) => {
    setSelectedAppForQuestionnaire(application);
    setQuestionnaireDialogOpen(true);
  };

  const getStatusColor = (status) => {
    const s = status?.toLowerCase();
    if (s === 'confirmed' || s === 'confirm-payment') return 'success';
    if (s === 'accepted' || s === 'approved') return 'info';
    if (s === 'rejected') return 'error';
    if (s === 'pending') return 'warning';
    return 'default';
  };

  if (eventLoading || applicationsLoading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (eventError || applicationsError) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          Error loading data: {eventError?.message || applicationsError?.message}
        </Alert>
      </Container>
    );
  }

  if (!event) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">Event not found</Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', pb: 4 }}>
      <Container maxWidth="xl" sx={{ pt: 4 }}>
        {/* Professional CRM Header */}
        <Paper 
          elevation={0} 
          sx={{ 
            mb: 3, 
            background: 'linear-gradient(135deg, #0f172a 0%, #334155 100%)',
            color: 'white',
            borderRadius: 1,
            overflow: 'hidden',
            border: '1px solid rgba(255,255,255,0.1)'
          }}
        >
          <Box sx={{ p: { xs: 2.5, md: 3.5 } }}>
            {/* Navigation and Context */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <IconButton 
                  onClick={() => history.goBack()} 
                  size="small"
                  sx={{ 
                    color: 'rgba(255,255,255,0.9)',
                    bgcolor: 'rgba(255,255,255,0.1)',
                    '&:hover': { 
                      bgcolor: 'rgba(255,255,255,0.2)',
                    }
                  }}
                >
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: 'rgba(255,255,255,0.7)',
                    textTransform: 'uppercase',
                    letterSpacing: 1,
                    fontSize: '0.7rem'
                  }}
                >
                  Vendor Applications
                </Typography>
              </Box>
            </Box>

            {/* Event Information */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ flex: 1, minWidth: { xs: '100%', md: 'auto' } }}>
                <Typography 
                  variant="h5" 
                  component="h1" 
                  sx={{ 
                    color: 'white', 
                    fontWeight: 600,
                    mb: 1.5,
                    fontSize: { xs: '1.35rem', md: '1.5rem' },
                    lineHeight: 1.3
                  }}
                >
                  {event.title}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'rgba(255,255,255,0.8)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      fontSize: '0.875rem'
                    }}
                  >
                    <CalendarIcon sx={{ fontSize: 18, opacity: 0.9 }} />
                    {format(new Date(event.date), 'MMMM d, yyyy')}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'rgba(255,255,255,0.8)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      fontSize: '0.875rem'
                    }}
                  >
                    <LocationIcon sx={{ fontSize: 18, opacity: 0.9 }} />
                    {event.location}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* Application Metrics - Consolidated Summary */}
        <Paper 
          elevation={2} 
          sx={{ 
            mb: 3, 
            bgcolor: 'white',
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
            p: 2.5
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <PeopleIcon sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Application Overview
            </Typography>
          </Box>
          
          <Grid container spacing={3}>
            {/* Total Applicants - Main Metric */}
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" sx={{ fontWeight: 700, color: theme.palette.primary.main, mb: 0.5 }}>
                  {stats.total}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                  Total Applicants
                </Typography>
              </Box>
            </Grid>
            
            {/* Confirmed / Capacity */}
            {stats.capacity > 0 && (
              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 0.5, mb: 0.5 }}>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: '#0288d1' }}>
                      {stats.confirmed}
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                      /{stats.capacity}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', fontWeight: 500, mb: 1, textAlign: 'center' }}>
                    Confirmed
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ 
                      flex: 1, 
                      height: 6, 
                      bgcolor: alpha('#0288d1', 0.1), 
                      borderRadius: 3,
                      overflow: 'hidden'
                    }}>
                      <Box sx={{ 
                        width: `${Math.min(stats.capacityUtilization, 100)}%`, 
                        height: '100%', 
                        bgcolor: '#0288d1',
                        borderRadius: 3,
                        transition: 'width 0.3s'
                      }} />
                    </Box>
                    <Typography variant="caption" sx={{ color: '#0288d1', fontWeight: 600, minWidth: 35 }}>
                      {stats.capacityUtilization.toFixed(0)}%
                    </Typography>
                  </Box>
                  {stats.paymentPending > 0 && (
                    <Typography variant="caption" sx={{ color: '#ed6c02', mt: 0.5, display: 'block', textAlign: 'center', fontWeight: 500 }}>
                      {stats.paymentPending} payment pending
                    </Typography>
                  )}
                </Box>
              </Grid>
            )}
            
            {/* Pending Review */}
            <Grid item xs={12} sm={6} md={stats.capacity > 0 ? 3 : 4.5}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" sx={{ fontWeight: 700, color: '#ed6c02', mb: 0.5 }}>
                  {stats.pending}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                  Awaiting Review
                </Typography>
                {stats.total > 0 && (
                  <Typography variant="caption" sx={{ color: '#ed6c02', mt: 0.5, display: 'block', fontWeight: 500 }}>
                    {((stats.pending / stats.total) * 100).toFixed(0)}% of total
                  </Typography>
                )}
              </Box>
            </Grid>
            
            {/* Rejected */}
            <Grid item xs={12} sm={6} md={stats.capacity > 0 ? 3 : 4.5}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" sx={{ fontWeight: 700, color: '#d32f2f', mb: 0.5 }}>
                  {stats.rejected}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                  Declined
                </Typography>
                {stats.total > 0 && (
                  <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, display: 'block', fontWeight: 500 }}>
                    {((stats.rejected / stats.total) * 100).toFixed(0)}% of total
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
          
          {/* Tier Distribution - Integrated */}
          {activeTab !== 6 && Object.keys(tierStats).length > 0 && (
            <>
              <Divider sx={{ my: 2.5 }} />
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <EventSeatIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Tier Distribution
                  </Typography>
                </Box>
                <Grid container spacing={3}>
                  {Object.values(tierStats)
                    .sort((a, b) => b.total - a.total)
                    .map((tier) => {
                      return (
                        <Grid item xs={12} sm={6} md={4} key={tier.id}>
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                              bgcolor: alpha(theme.palette.primary.main, 0.02),
                              transition: 'all 0.2s',
                              '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.04),
                                borderColor: alpha(theme.palette.primary.main, 0.3),
                              }
                            }}
                          >
                            {/* Tier Header */}
                            <Box sx={{ textAlign: 'center', mb: 2 }}>
                              <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                                {tier.name}
                              </Typography>
                              {tier.fee !== undefined && tier.fee !== null && (
                                <Typography variant="body1" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                                  ${tier.fee?.toFixed(2) || '0.00'}
                                </Typography>
                              )}
                              {tier.capacity !== null && tier.capacity !== undefined && (
                                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                                  Capacity: {tier.confirmed}/{tier.capacity} spots
                                </Typography>
                              )}
                              <Chip 
                                label={`${tier.total} total`} 
                                size="small" 
                                color="primary" 
                                sx={{ 
                                  mt: 1,
                                  height: 24, 
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                }}
                              />
                            </Box>
                            
                            {/* Confirmed Count */}
                            <Box sx={{ textAlign: 'center', mt: 1 }}>
                              <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.success.main, mb: 0.5 }}>
                                {tier.confirmed}
                              </Typography>
                              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem', fontWeight: 500 }}>
                                Confirmed
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      );
                    })}
                </Grid>
              </Box>
            </>
          )}
        </Paper>

        {/* Capacity Error Alert */}
        {capacityError && (
          <Alert severity="error" onClose={() => setCapacityError('')} sx={{ mb: 2 }}>
            {capacityError}
          </Alert>
        )}

        {/* Tabs */}
        <Paper elevation={2} sx={{ mb: 2, bgcolor: 'white' }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}
          >
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>All</span>
                  <Chip label={stats.total} size="small" sx={{ height: 20 }} />
                </Box>
              } 
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>Pending</span>
                  <Badge badgeContent={stats.pending} color="warning">
                    <Chip label={stats.pending} size="small" color="warning" sx={{ height: 20 }} />
                  </Badge>
                </Box>
              } 
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>Payment Pending</span>
                  <Badge badgeContent={stats.paymentPending} color="warning">
                    <Chip label={stats.paymentPending} size="small" color="warning" sx={{ height: 20 }} />
                  </Badge>
                </Box>
              } 
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>Confirmed</span>
                  <Badge badgeContent={stats.confirmed} color="success">
                    <Chip label={stats.confirmed} size="small" color="success" sx={{ height: 20 }} />
                  </Badge>
                </Box>
              } 
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>Rejected</span>
                  <Badge badgeContent={stats.rejected} color="error">
                    <Chip label={stats.rejected} size="small" color="error" sx={{ height: 20 }} />
                  </Badge>
                </Box>
              } 
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>Waitlist</span>
                  <Badge badgeContent={stats.waitlist} color="info">
                    <Chip label={stats.waitlist} size="small" color="info" sx={{ height: 20 }} />
                  </Badge>
                </Box>
              } 
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AnalyticsIcon sx={{ fontSize: 18 }} />
                  <span>Statistics</span>
                </Box>
              } 
            />
          </Tabs>
        </Paper>

        {/* Statistics Tab Content */}
        {activeTab === 6 && (
          <Box sx={{ mb: 3 }}>
            {applications && applications.length > 0 ? (
              <Grid container spacing={3}>
                {/* Vendor Categories Pie Chart */}
                {categoryPieData.length > 0 ? (
                  <Grid item xs={12} md={6}>
                    <Paper 
                      elevation={3} 
                      sx={{ 
                        p: 3, 
                        bgcolor: 'white', 
                        height: '100%',
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.divider, 0.5)}`
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                        <AssessmentIcon sx={{ color: theme.palette.primary.main, fontSize: 28 }} />
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                          Vendor Categories Distribution
                        </Typography>
                      </Box>
                      <ResponsiveContainer width="100%" height={350}>
                        <PieChart>
                          <Pie
                            data={categoryPieData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, value, percent }) => 
                              `${name.length > 15 ? name.substring(0, 15) + '...' : name}\n${value} (${(percent * 100).toFixed(1)}%)`
                            }
                            outerRadius={110}
                            innerRadius={40}
                            fill="#8884d8"
                            dataKey="value"
                            paddingAngle={2}
                          >
                            {categoryPieData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <RechartsTooltip 
                            formatter={(value, name, props) => [
                              `${value} vendors (${((value / stats.total) * 100).toFixed(1)}%)`,
                              props.payload.name
                            ]}
                          />
                          <Legend 
                            verticalAlign="bottom" 
                            height={36}
                            formatter={(value, entry) => (
                              <span style={{ color: entry.color, fontWeight: 500 }}>
                                {value}
                              </span>
                            )}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                      <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}>
                        <Typography variant="caption" color="text.secondary">
                          Total Categories: {categoryPieData.length} | Total Vendors: {stats.total}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                ) : (
                  <Grid item xs={12} md={6}>
                    <Paper elevation={3} sx={{ p: 3, bgcolor: 'white', height: '100%', borderRadius: 2 }}>
                      <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                        No vendor category data available
                      </Typography>
                    </Paper>
                  </Grid>
                )}

                {/* Status Distribution Pie Chart */}
                {statusPieData.length > 0 ? (
                  <Grid item xs={12} md={6}>
                    <Paper 
                      elevation={3} 
                      sx={{ 
                        p: 3, 
                        bgcolor: 'white', 
                        height: '100%',
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.divider, 0.5)}`
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                        <AnalyticsIcon sx={{ color: theme.palette.primary.main, fontSize: 28 }} />
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                          Application Status Distribution
                        </Typography>
                      </Box>
                      <ResponsiveContainer width="100%" height={350}>
                        <PieChart>
                          <Pie
                            data={statusPieData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, value, percent }) => 
                              `${name}\n${value} (${(percent * 100).toFixed(1)}%)`
                            }
                            outerRadius={110}
                            innerRadius={40}
                            fill="#8884d8"
                            dataKey="value"
                            paddingAngle={2}
                          >
                            {statusPieData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <RechartsTooltip 
                            formatter={(value, name, props) => [
                              `${value} applications (${((value / stats.total) * 100).toFixed(1)}%)`,
                              props.payload.name
                            ]}
                          />
                          <Legend 
                            verticalAlign="bottom" 
                            height={36}
                            formatter={(value, entry) => (
                              <span style={{ color: entry.color, fontWeight: 500 }}>
                                {value}
                              </span>
                            )}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                      <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}>
                        <Typography variant="caption" color="text.secondary">
                          Total Applications: {stats.total} | Reviewed: {stats.total - stats.pending}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                ) : (
                  <Grid item xs={12} md={6}>
                    <Paper elevation={3} sx={{ p: 3, bgcolor: 'white', height: '100%', borderRadius: 2 }}>
                      <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                        No status data available
                      </Typography>
                    </Paper>
                  </Grid>
                )}

                {/* Categories by Status Bar Chart */}
                {categoryBarData.length > 0 && (
                  <Grid item xs={12}>
                    <Paper 
                      elevation={3} 
                      sx={{ 
                        p: 3, 
                        bgcolor: 'white',
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.divider, 0.5)}`
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                        <TableChartIcon sx={{ color: theme.palette.primary.main, fontSize: 28 }} />
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                          Applications by Category & Status
                        </Typography>
                      </Box>
                      <ResponsiveContainer width="100%" height={350}>
                        <BarChart 
                          data={categoryBarData}
                          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
                          <XAxis 
                            dataKey="name" 
                            angle={-45}
                            textAnchor="end"
                            height={80}
                            tick={{ fontSize: 12 }}
                          />
                          <YAxis tick={{ fontSize: 12 }} />
                          <RechartsTooltip 
                            cursor={{ fill: alpha(theme.palette.primary.main, 0.1) }}
                            formatter={(value, name) => [`${value} applications`, name]}
                          />
                          <Legend 
                            wrapperStyle={{ paddingTop: 20 }}
                            formatter={(value) => <span style={{ fontWeight: 500 }}>{value}</span>}
                          />
                          <Bar 
                            dataKey="Pending" 
                            stackId="a" 
                            fill={theme.palette.warning.main}
                            radius={[0, 0, 0, 0]}
                          />
                          <Bar 
                            dataKey="Accepted" 
                            stackId="a" 
                            fill={theme.palette.success.main}
                            radius={[0, 0, 0, 0]}
                          />
                          <Bar 
                            dataKey="Rejected" 
                            stackId="a" 
                            fill={theme.palette.error.main}
                            radius={[4, 4, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </Paper>
                  </Grid>
                )}

                {/* Application Timeline */}
                {timelineData.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Paper 
                      elevation={3} 
                      sx={{ 
                        p: 3, 
                        bgcolor: 'white', 
                        height: '100%',
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.divider, 0.5)}`
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                        <CalendarIcon sx={{ color: theme.palette.primary.main, fontSize: 28 }} />
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                          Application Timeline
                        </Typography>
                        <Chip 
                          label="Last 7 Days" 
                          size="small" 
                          variant="outlined" 
                          sx={{ ml: 'auto' }}
                        />
                      </Box>
                      <ResponsiveContainer width="100%" height={300}>
                        <AreaChart 
                          data={timelineData}
                          margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                        >
                          <defs>
                            <linearGradient id="colorApplications" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.3}/>
                              <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
                          <XAxis 
                            dataKey="date" 
                            tick={{ fontSize: 12 }}
                            angle={-45}
                            textAnchor="end"
                            height={60}
                          />
                          <YAxis tick={{ fontSize: 12 }} />
                          <RechartsTooltip 
                            formatter={(value) => [`${value} applications`, 'Applications']}
                            labelStyle={{ fontWeight: 600 }}
                          />
                          <Area 
                            type="monotone" 
                            dataKey="applications" 
                            stroke={theme.palette.primary.main} 
                            strokeWidth={2}
                            fillOpacity={1}
                            fill="url(#colorApplications)" 
                          />
                          <Line 
                            type="monotone" 
                            dataKey="applications" 
                            stroke={theme.palette.primary.dark}
                            strokeWidth={2}
                            dot={{ fill: theme.palette.primary.main, r: 4 }}
                            activeDot={{ r: 6 }}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                      <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="caption" color="text.secondary">
                            Total in period: {timelineData.reduce((sum, item) => sum + item.applications, 0)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Avg per day: {(timelineData.reduce((sum, item) => sum + item.applications, 0) / timelineData.length).toFixed(1)}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                )}

                {/* Category Breakdown Table */}
                {Object.keys(categoryStats).length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Paper 
                      elevation={3} 
                      sx={{ 
                        p: 3, 
                        bgcolor: 'white', 
                        height: '100%',
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.divider, 0.5)}`
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <AssessmentIcon sx={{ color: theme.palette.primary.main, fontSize: 28 }} />
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                          Category Breakdown
                        </Typography>
                      </Box>
                      <TableContainer sx={{ maxHeight: 400 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                              <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Category</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Total</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Pending</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Approved</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Rejected</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Rate</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {Object.entries(categoryStats)
                              .sort((a, b) => b[1].total - a[1].total)
                              .map(([category, catStats]) => {
                                const approvalRate = catStats.total > 0 
                                  ? ((catStats.accepted / catStats.total) * 100).toFixed(0)
                                  : 0;
                                return (
                                  <TableRow 
                                    key={category} 
                                    hover
                                    sx={{ 
                                      '&:hover': { 
                                        bgcolor: alpha(theme.palette.primary.main, 0.04) 
                                      } 
                                    }}
                                  >
                                    <TableCell sx={{ fontWeight: 500 }}>{category}</TableCell>
                                    <TableCell align="right">
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {catStats.total}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      {catStats.pending > 0 ? (
                                        <Chip 
                                          label={catStats.pending} 
                                          size="small" 
                                          color="warning" 
                                          variant="outlined"
                                          sx={{ fontWeight: 500 }}
                                        />
                                      ) : (
                                        <Typography variant="body2" color="text.secondary">—</Typography>
                                      )}
                                    </TableCell>
                                    <TableCell align="right">
                                      {catStats.accepted > 0 ? (
                                        <Chip 
                                          label={catStats.accepted} 
                                          size="small" 
                                          color="success" 
                                          variant="filled"
                                          sx={{ fontWeight: 500 }}
                                        />
                                      ) : (
                                        <Typography variant="body2" color="text.secondary">—</Typography>
                                      )}
                                    </TableCell>
                                    <TableCell align="right">
                                      {catStats.rejected > 0 ? (
                                        <Chip 
                                          label={catStats.rejected} 
                                          size="small" 
                                          color="error" 
                                          variant="outlined"
                                          sx={{ fontWeight: 500 }}
                                        />
                                      ) : (
                                        <Typography variant="body2" color="text.secondary">—</Typography>
                                      )}
                                    </TableCell>
                                    <TableCell align="right">
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                        <Typography 
                                          variant="body2" 
                                          sx={{ 
                                            fontWeight: 600,
                                            color: approvalRate >= 50 ? theme.palette.success.main : 
                                                   approvalRate >= 25 ? theme.palette.warning.main : 
                                                   theme.palette.error.main
                                          }}
                                        >
                                          {approvalRate}%
                                        </Typography>
                                        <TrendingUpIcon 
                                          sx={{ 
                                            fontSize: 14, 
                                            color: approvalRate >= 50 ? theme.palette.success.main : 'transparent'
                                          }} 
                                        />
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  </Grid>
                )}

                {/* Tier Statistics */}
                {event?.table_tiers && event.table_tiers.length > 0 && Object.keys(tierStats).length > 0 && (
                  <>
                    {/* Tier Distribution Bar Chart */}
                    {tierBarData.length > 0 && (
                      <Grid item xs={12}>
                        <Paper 
                          elevation={3} 
                          sx={{ 
                            p: 3, 
                            bgcolor: 'white',
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.divider, 0.5)}`
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                            <EventSeatIcon sx={{ color: theme.palette.primary.main, fontSize: 28 }} />
                            <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                              Applications by Table/Booth Tier
                            </Typography>
                          </Box>
                          <ResponsiveContainer width="100%" height={350}>
                            <BarChart 
                              data={tierBarData}
                              margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
                              <XAxis 
                                dataKey="name" 
                                angle={-45}
                                textAnchor="end"
                                height={80}
                                tick={{ fontSize: 12 }}
                              />
                              <YAxis tick={{ fontSize: 12 }} />
                              <RechartsTooltip 
                                cursor={{ fill: alpha(theme.palette.primary.main, 0.1) }}
                                formatter={(value, name) => [`${value} applications`, name]}
                              />
                              <Legend 
                                wrapperStyle={{ paddingTop: 20 }}
                                formatter={(value) => <span style={{ fontWeight: 500 }}>{value}</span>}
                              />
                              <Bar 
                                dataKey="Confirmed" 
                                stackId="a" 
                                fill={theme.palette.success.main}
                                radius={[0, 0, 0, 0]}
                              />
                              <Bar 
                                dataKey="Payment Pending" 
                                stackId="a" 
                                fill="#FFA726"
                                radius={[0, 0, 0, 0]}
                              />
                              <Bar 
                                dataKey="Pending" 
                                stackId="a" 
                                fill={theme.palette.warning.main}
                                radius={[0, 0, 0, 0]}
                              />
                              <Bar 
                                dataKey="Rejected" 
                                stackId="a" 
                                fill={theme.palette.error.main}
                                radius={[4, 4, 0, 0]}
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        </Paper>
                      </Grid>
                    )}

                    {/* Tier Breakdown Table */}
                    <Grid item xs={12}>
                      <Paper 
                        elevation={3} 
                        sx={{ 
                          p: 3, 
                          bgcolor: 'white', 
                          borderRadius: 2,
                          border: `1px solid ${alpha(theme.palette.divider, 0.5)}`
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <EventSeatIcon sx={{ color: theme.palette.primary.main, fontSize: 28 }} />
                          <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                            Tier Statistics & Payment Status
                          </Typography>
                        </Box>
                        <TableContainer>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Tier Name</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Fee</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Total</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Confirmed</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Payment Pending</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Pending</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Rejected</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Waitlist</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {Object.values(tierStats)
                                .sort((a, b) => b.total - a.total)
                                .map((tier) => (
                                  <TableRow 
                                    key={tier.id} 
                                    hover
                                    sx={{ 
                                      '&:hover': { 
                                        bgcolor: alpha(theme.palette.primary.main, 0.04) 
                                      } 
                                    }}
                                  >
                                    <TableCell>
                                      <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                          {tier.name}
                                        </Typography>
                                        {tier.description && (
                                          <Typography variant="caption" color="text.secondary">
                                            {tier.description}
                                          </Typography>
                                        )}
                                      </Box>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Typography 
                                        variant="body2" 
                                        sx={{ 
                                          fontWeight: 600,
                                          color: parseFloat(tier.fee) === 0 ? theme.palette.success.main : theme.palette.text.primary
                                        }}
                                      >
                                        {parseFloat(tier.fee) === 0 ? 'Free' : `$${parseFloat(tier.fee).toFixed(2)}`}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {tier.total}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      {tier.confirmed > 0 ? (
                                        <Chip 
                                          label={tier.confirmed} 
                                          size="small" 
                                          color="success" 
                                          variant="filled"
                                          sx={{ fontWeight: 600 }}
                                        />
                                      ) : (
                                        <Typography variant="body2" color="text.secondary">—</Typography>
                                      )}
                                    </TableCell>
                                    <TableCell align="right">
                                      {tier.paymentPending > 0 ? (
                                        <Chip 
                                          label={tier.paymentPending} 
                                          size="small" 
                                          color="warning" 
                                          variant="outlined"
                                          sx={{ fontWeight: 500 }}
                                        />
                                      ) : (
                                        <Typography variant="body2" color="text.secondary">—</Typography>
                                      )}
                                    </TableCell>
                                    <TableCell align="right">
                                      {tier.pending > 0 ? (
                                        <Chip 
                                          label={tier.pending} 
                                          size="small" 
                                          color="info" 
                                          variant="outlined"
                                          sx={{ fontWeight: 500 }}
                                        />
                                      ) : (
                                        <Typography variant="body2" color="text.secondary">—</Typography>
                                      )}
                                    </TableCell>
                                    <TableCell align="right">
                                      {tier.rejected > 0 ? (
                                        <Chip 
                                          label={tier.rejected} 
                                          size="small" 
                                          color="error" 
                                          variant="outlined"
                                          sx={{ fontWeight: 500 }}
                                        />
                                      ) : (
                                        <Typography variant="body2" color="text.secondary">—</Typography>
                                      )}
                                    </TableCell>
                                    <TableCell align="right">
                                      {tier.waitlist > 0 ? (
                                        <Chip 
                                          label={tier.waitlist} 
                                          size="small" 
                                          color="info" 
                                          variant="outlined"
                                          sx={{ fontWeight: 500 }}
                                        />
                                      ) : (
                                        <Typography variant="body2" color="text.secondary">—</Typography>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Paper>
                    </Grid>
                  </>
                )}
              </Grid>
            ) : (
              <Paper elevation={2} sx={{ p: 4, bgcolor: 'white', textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  Charts and statistics will appear here once you have applications from vendors.
                </Typography>
              </Paper>
            )}
          </Box>
        )}


        {/* Filters and Bulk Actions Toolbar - Only show for application tabs */}
        {activeTab !== 6 && (
          <Paper elevation={2} sx={{ p: 2, mb: 2, bgcolor: 'white' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search vendors by name, contact, or notes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                sx={{ bgcolor: '#f9f9f9' }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small" sx={{ bgcolor: '#f9f9f9' }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="accepted">Approved</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small" sx={{ bgcolor: '#f9f9f9' }}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={categoryFilter}
                  label="Category"
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <MenuItem value="all">All Categories</MenuItem>
                  {availableCategories.map(cat => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small" sx={{ bgcolor: '#f9f9f9' }}>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <MenuItem value="date">Application Date</MenuItem>
                  <MenuItem value="name">Vendor Name</MenuItem>
                  <MenuItem value="category">Category</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={1}>
              <Tooltip title={sortOrder === 'desc' ? 'Sort Ascending' : 'Sort Descending'}>
                <IconButton
                  onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                  sx={{ 
                    bgcolor: '#f9f9f9',
                    border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                    width: '100%',
                    height: '40px'
                  }}
                >
                  {sortOrder === 'desc' ? <ArrowDownIcon /> : <ArrowUpIcon />}
                </IconButton>
              </Tooltip>
            </Grid>
            <Grid item xs={12} md={3} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Tooltip title="Send Mass Email to Vendors">
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<SendIcon />}
                  onClick={() => {
                    setMassEmailData({
                      subject: '',
                      message: '',
                      recipient_filter: 'all'
                    });
                    setMassEmailDialogOpen(true);
                  }}
                  disabled={!applications || applications.length === 0}
                >
                  Email Vendors
                </Button>
              </Tooltip>
              <Tooltip title="Export to CSV">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FileDownloadIcon />}
                  onClick={() => {
                    // Export functionality
                    const csv = [
                      ['Vendor Name', 'Category', 'Email', 'Contact', 'Status', 'Applied Date', 'Table/Setup #'],
                      ...filteredApplications.map(app => [
                        app.vendor?.business_name || '',
                        app.vendor?.category_name || 'Uncategorized',
                        app.vendor?.email || '',
                        app.vendor?.contact_info || '',
                        app.status || 'Pending',
                        format(new Date(app.created_at), 'yyyy-MM-dd'),
                        app.table_id || '',
                      ])
                    ].map(row => row.join(',')).join('\n');
                    
                    const blob = new Blob([csv], { type: 'text/csv' });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `event-${eventId}-applications-${format(new Date(), 'yyyy-MM-dd')}.csv`;
                    a.click();
                    window.URL.revokeObjectURL(url);
                  }}
                  disabled={filteredApplications.length === 0}
                >
                  Export
                </Button>
              </Tooltip>
            </Grid>
            {selectedApplications.length > 0 && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', bgcolor: alpha(theme.palette.primary.main, 0.1), p: 1.5, borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mr: 2 }}>
                    {selectedApplications.length} selected
                  </Typography>
                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    startIcon={<AcceptIcon />}
                    onClick={() => handleBulkAction('accepted')}
                    disabled={bulkUpdateMutation.isLoading}
                  >
                    Approve Selected
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    startIcon={<RejectIcon />}
                    onClick={() => handleBulkAction('rejected')}
                    disabled={bulkUpdateMutation.isLoading}
                  >
                    Reject Selected
                  </Button>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setSelectedApplications([])}
                  >
                    Clear Selection
                  </Button>
                </Box>
              </Grid>
            )}
          </Grid>
        </Paper>
        )}

        {/* Applications Table - Only show for application tabs */}
        {activeTab !== 6 && (applicationsLoading ? (
          <Paper elevation={2} sx={{ p: 8, textAlign: 'center', bgcolor: 'white' }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Loading applications...
            </Typography>
          </Paper>
        ) : filteredApplications.length === 0 ? (
          <Paper elevation={2} sx={{ p: 8, textAlign: 'center', bgcolor: 'white' }}>
            <StoreIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.3 }} />
            <Typography variant="h6" color="text.secondary">
              No applications found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {searchQuery || statusFilter !== 'all' || activeTab !== 0 
                ? 'Try adjusting your filters or tabs' 
                : `No vendors have applied to this event yet. Total applications in database: ${applications?.length || 0}`}
            </Typography>
            {applicationsError && (
              <Alert severity="error" sx={{ mt: 2, maxWidth: 600, mx: 'auto' }}>
                Error loading applications: {applicationsError.message}
              </Alert>
            )}
          </Paper>
        ) : (
          <Paper elevation={2} sx={{ overflow: 'hidden', bgcolor: 'white' }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1, 
              p: 2, 
              bgcolor: '#fafafa',
              borderBottom: `2px solid ${alpha(theme.palette.divider, 0.5)}` 
            }}>
              <Checkbox
                checked={selectedApplications.length === filteredApplications.length && filteredApplications.length > 0}
                indeterminate={selectedApplications.length > 0 && selectedApplications.length < filteredApplications.length}
                onChange={handleSelectAll}
              />
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                Select All ({selectedApplications.length} of {filteredApplications.length} selected)
              </Typography>
            </Box>
            <TableContainer sx={{ overflowX: 'auto', position: 'relative' }}>
              <Table sx={{ minWidth: 1200 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell padding="checkbox" sx={{ fontWeight: 'bold', width: 48 }}></TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 180 }}>Vendor</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>Category</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 180 }}>Contact</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 140 }}>Payment</TableCell>
                    {event?.table_tiers && event.table_tiers.length > 0 && (
                      <TableCell sx={{ fontWeight: 'bold', minWidth: 140 }}>Selected Tier</TableCell>
                    )}
                    {event?.custom_checkboxes && event.custom_checkboxes.length > 0 && (
                      <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>Selected Options</TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>Table/Setup #</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 130, maxWidth: 130, width: 130 }}>Applied</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', minWidth: 220, whiteSpace: 'nowrap', position: 'sticky', right: 0, bgcolor: '#f5f5f5 !important', zIndex: 11, boxShadow: '-2px 0 4px rgba(0,0,0,0.1)' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredApplications.map((application) => {
                    const vendor = application.vendor || {};
                    const isSelected = selectedApplications.includes(application.application_id);
                    const status = application.status?.toLowerCase();
                    return (
                      <TableRow 
                        key={application.application_id} 
                        hover 
                        selected={isSelected}
                        sx={{ 
                          '&:hover': { 
                            bgcolor: alpha(theme.palette.primary.main, 0.04),
                            '& td:last-child': { 
                              backgroundColor: `${alpha(theme.palette.primary.main, 0.04)} !important`,
                              boxShadow: '-2px 0 4px rgba(0,0,0,0.1)'
                            }
                          },
                          '&.Mui-selected': { 
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                            '& td:last-child': { 
                              backgroundColor: `${alpha(theme.palette.primary.main, 0.08)} !important`,
                              boxShadow: '-2px 0 4px rgba(0,0,0,0.1)'
                            }
                          }
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isSelected}
                            onChange={() => handleSelectApplication(application.application_id)}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar 
                              src={vendor.profile_picture_url ? getImageUrl(vendor.profile_picture_url) : null}
                              sx={{ width: 40, height: 40 }}
                            >
                              <StoreIcon />
                            </Avatar>
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 'medium' }} noWrap>
                                {vendor.business_name || 'Unknown Vendor'}
                              </Typography>
                              {vendor.bio && (
                                <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 200, display: 'block' }}>
                                  {vendor.bio}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={vendor.category_name || 'Uncategorized'} 
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.75rem' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box>
                            {vendor.email && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                <EmailIcon fontSize="small" color="primary" />
                                <Typography variant="body2" color="text.primary" noWrap sx={{ maxWidth: 200 }}>
                                  {vendor.email}
                                </Typography>
                              </Box>
                            )}
                            {vendor.contact_info && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PhoneIcon fontSize="small" color="action" />
                                <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                                  {vendor.contact_info}
                                </Typography>
                              </Box>
                            )}
                            {!vendor.email && !vendor.contact_info && (
                              <Typography variant="body2" color="text.secondary">
                                —
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Chip 
                              label={(() => {
                                const s = application.status;
                                // Check if status is Waitlist or has waitlist position
                                if (!s) {
                                  // If no status but has waitlist position, show as Waitlist
                                  if (application.waitlist_position !== null && application.waitlist_position !== undefined) {
                                    return 'Waitlist';
                                  }
                                  return 'Pending';
                                }
                                const sLower = s.toLowerCase();
                                // Normalize status display - handle any variations
                                if (sLower === 'waitlist') {
                                  return 'Waitlist';
                                }
                                if (application.waitlist_position !== null && application.waitlist_position !== undefined) {
                                  return 'Waitlist';
                                }
                                if (sLower === 'confirmed' || sLower === 'confirm-payment') return 'Confirmed';
                                return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
                              })()} 
                              color={(() => {
                                const s = application.status?.toLowerCase();
                                // If status is Waitlist or has waitlist position, return 'info' for waitlist color
                                if (s === 'waitlist' || (application.waitlist_position !== null && application.waitlist_position !== undefined)) {
                                  return 'info';
                                }
                                return getStatusColor(application.status);
                              })()}
                              size="small"
                              sx={{ fontWeight: 'medium' }}
                            />
                            {application.waitlist_position !== null && application.waitlist_position !== undefined && (
                              <Typography variant="caption" color="info.main" sx={{ display: 'block', mt: 0.5 }}>
                                Position #{application.waitlist_position}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const statusLower = application.status?.toLowerCase();
                            const paymentStatus = application.payment_status?.toLowerCase();
                            const isConfirmed = (statusLower === 'confirmed' || statusLower === 'confirm-payment');
                            const isPaymentConfirmed = (paymentStatus === 'confirmed');
                            
                            if (isConfirmed || isPaymentConfirmed) {
                              return <Chip label="Paid" color="success" size="small" />;
                            } else if (application.payment_status === 'pending' && 
                                       (application.status?.toLowerCase() === 'accepted' || application.status?.toLowerCase() === 'approved')) {
                              return (
                                <Box>
                                  <Chip label="Payment Pending" color="warning" size="small" />
                                  {application.payment_deadline && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                      Due: {format(new Date(application.payment_deadline), 'MMM d')}
                                    </Typography>
                                  )}
                                </Box>
                              );
                            } else {
                              return <Chip label="N/A" size="small" variant="outlined" />;
                            }
                          })()}
                        </TableCell>
                        {/* Selected Tier Column */}
                        {event?.table_tiers && event.table_tiers.length > 0 && (
                          <TableCell>
                            {editingAssignment?.applicationId === application.application_id && editingAssignment?.field === 'tier' ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <FormControl size="small" sx={{ minWidth: 150 }}>
                                  <Select
                                    value={editValues[application.application_id]?.tier ?? application.selected_tier_id ?? ''}
                                    onChange={(e) => handleEditValueChange(application.application_id, 'tier', e.target.value)}
                                    autoFocus
                                  >
                                    <MenuItem value="">
                                      <em>None</em>
                                    </MenuItem>
                                    {event.table_tiers.map((tier) => (
                                      <MenuItem key={tier.id} value={tier.id}>
                                        {tier.name} - ${tier.fee?.toFixed(2) || '0.00'}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleSaveAssignment(application.application_id)}
                                  disabled={updateAssignmentMutation.isLoading}
                                >
                                  <CheckIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="default"
                                  onClick={handleCancelEdit}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            ) : (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {application.selected_tier_id ? (() => {
                                  const selectedTier = event.table_tiers.find(tier => tier.id === application.selected_tier_id);
                                  if (selectedTier) {
                                    return (
                                      <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                          {selectedTier.name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          ${selectedTier.fee?.toFixed(2) || '0.00'}
                                        </Typography>
                                        {selectedTier.description && (
                                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                            {selectedTier.description}
                                          </Typography>
                                        )}
                                      </Box>
                                    );
                                  }
                                  return <Typography variant="body2" color="text.secondary">—</Typography>;
                                })() : (
                                  <Typography variant="body2" color="text.secondary">—</Typography>
                                )}
                                <Tooltip title="Edit tier selection">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleStartEdit(application.application_id, 'tier', application.selected_tier_id)}
                                    sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            )}
                          </TableCell>
                        )}
                        {/* Selected Checkboxes Column */}
                        {event?.custom_checkboxes && event.custom_checkboxes.length > 0 && (
                          <TableCell>
                            {application.selected_checkboxes && application.selected_checkboxes.length > 0 ? (
                              <Box>
                                {application.selected_checkboxes.map((checkboxId) => {
                                  const checkbox = event.custom_checkboxes.find(cb => cb.id === checkboxId);
                                  if (checkbox) {
                                    return (
                                      <Chip
                                        key={checkboxId}
                                        label={checkbox.label}
                                        size="small"
                                        sx={{ mb: 0.5, mr: 0.5 }}
                                        color="primary"
                                        variant="outlined"
                                      />
                                    );
                                  }
                                  return null;
                                })}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">—</Typography>
                            )}
                          </TableCell>
                        )}
                        <TableCell>
                          {editingAssignment?.applicationId === application.application_id && editingAssignment?.field === 'table' ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <TextField
                                type="number"
                                size="small"
                                value={editValues[application.application_id]?.table ?? application.table_id ?? ''}
                                onChange={(e) => handleEditValueChange(application.application_id, 'table', e.target.value)}
                                sx={{ width: 80 }}
                                inputProps={{ min: 0, style: { textAlign: 'center', padding: '4px' } }}
                                autoFocus
                              />
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleSaveAssignment(application.application_id)}
                                disabled={updateAssignmentMutation.isLoading}
                              >
                                <CheckIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="default"
                                onClick={handleCancelEdit}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="body2" color="text.secondary">
                                {application.table_id || (
                                  <Chip label="Not assigned" size="small" variant="outlined" color="default" sx={{ fontSize: '0.7rem' }} />
                                )}
                              </Typography>
                              <Tooltip title="Edit table number">
                                <IconButton
                                  size="small"
                                  onClick={() => handleStartEdit(application.application_id, 'table', application.table_id)}
                                  sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </TableCell>
                        <TableCell sx={{ 
                          minWidth: 130, 
                          maxWidth: 130, 
                          width: 130, 
                          overflow: 'hidden', 
                          paddingRight: '12px', 
                          borderRight: 'none',
                          position: 'relative',
                          clipPath: 'inset(0)'
                        }}>
                          <Box sx={{ width: '100%', overflow: 'hidden', maxWidth: '118px' }}>
                            <Typography variant="body2" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', width: '100%' }}>
                              {format(new Date(application.created_at), 'MMM d, yyyy')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', width: '100%' }}>
                              {format(new Date(application.created_at), 'h:mm a')}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ 
                          whiteSpace: 'nowrap', 
                          position: 'sticky', 
                          right: 0, 
                          backgroundColor: 'white !important', 
                          background: 'white !important',
                          zIndex: 10, 
                          minWidth: 220, 
                          width: 220, 
                          boxShadow: '-2px 0 4px rgba(0,0,0,0.1)',
                          paddingLeft: '12px',
                          overflow: 'visible'
                        }}>
                          <Box sx={{ 
                            display: 'flex', 
                            gap: 0.5, 
                            justifyContent: 'flex-end', 
                            alignItems: 'center', 
                            flexWrap: 'nowrap',
                            backgroundColor: 'white',
                            position: 'relative',
                            zIndex: 1
                          }}>
                            {/* Questionnaire Answers Indicator */}
                            {event?.custom_questions && event.custom_questions.length > 0 && 
                             application.question_answers && 
                             Object.keys(application.question_answers).length > 0 && (
                              <Tooltip title="View questionnaire answers">
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewQuestionnaire(application)}
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    bgcolor: alpha(theme.palette.info.main, 0.1),
                                    border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                                    '&:hover': {
                                      bgcolor: alpha(theme.palette.info.main, 0.2),
                                      border: `1px solid ${alpha(theme.palette.info.main, 0.5)}`,
                                    }
                                  }}
                                >
                                  <QuizIcon fontSize="small" sx={{ color: theme.palette.info.main }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="View full details">
                              <IconButton 
                                size="small" 
                                onClick={() => handleViewDetails(application)} 
                                color="primary"
                                sx={{ border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}` }}
                              >
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="View vendor profile">
                              <IconButton
                                size="small"
                                onClick={() => history.push(`/vendors/${vendor.vendor_id}`)}
                                sx={{ border: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}
                              >
                                <LinkIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Message vendor">
                              <IconButton
                                size="small"
                                onClick={() => handleMessageVendor(vendor.vendor_id, application.application_id)}
                                sx={{ 
                                  border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                                  bgcolor: alpha(theme.palette.info.main, 0.1),
                                  '&:hover': {
                                    bgcolor: alpha(theme.palette.info.main, 0.2),
                                  }
                                }}
                              >
                                <MessageIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {/* Quick actions for common status changes */}
                            {(!status || status === 'pending') && (
                              <>
                                <Tooltip title={
                                  (stats?.confirmed || 0) >= (event?.capacity || 0) && event?.capacity
                                    ? `Event capacity (${event.capacity}) is full`
                                    : "Approve application"
                                }>
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="success"
                                      onClick={() => handleAccept(application.application_id)}
                                      disabled={
                                        updateStatusMutation.isLoading || 
                                        ((stats?.confirmed || 0) >= (event?.capacity || 0) && event?.capacity)
                                      }
                                      sx={{ 
                                        border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                                        bgcolor: alpha(theme.palette.success.main, 0.1),
                                        '&:hover': {
                                          bgcolor: alpha(theme.palette.success.main, 0.2),
                                        }
                                      }}
                                    >
                                      <AcceptIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Reject application">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleReject(application.application_id)}
                                    disabled={updateStatusMutation.isLoading}
                                    sx={{ 
                                      border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                                      bgcolor: alpha(theme.palette.error.main, 0.1),
                                      '&:hover': {
                                        bgcolor: alpha(theme.palette.error.main, 0.2),
                                      }
                                    }}
                                  >
                                    <RejectIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                            {(status === 'accepted' || status === 'approved') && application.payment_status === 'pending' && (
                              <Tooltip title="Confirm Payment Received">
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() => confirmPaymentMutation.mutate(application.application_id)}
                                  disabled={confirmPaymentMutation.isLoading}
                                  sx={{ 
                                    border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                                    bgcolor: alpha(theme.palette.success.main, 0.1),
                                    '&:hover': {
                                      bgcolor: alpha(theme.palette.success.main, 0.2),
                                    }
                                  }}
                                >
                                  <MoneyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {/* Status change menu - available for all applications */}
                            <Tooltip title="Change status">
                              <IconButton
                                size="small"
                                onClick={(e) => handleStatusMenuOpen(e, application)}
                                disabled={updateStatusMutation.isLoading}
                                sx={{ 
                                  border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                                  '&:hover': {
                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                  }
                                }}
                              >
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ))}
      )}

      {/* Status Change Menu */}
      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={handleStatusMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {selectedAppForStatusChange && (
          <>
            <MenuItem 
              onClick={() => handleStatusChange('pending')}
              disabled={selectedAppForStatusChange.status?.toLowerCase() === 'pending'}
            >
              <ListItemIcon>
                <PendingIcon fontSize="small" color={selectedAppForStatusChange.status?.toLowerCase() === 'pending' ? 'disabled' : 'action'} />
              </ListItemIcon>
              <ListItemText primary="Set to Pending" secondary={selectedAppForStatusChange.status?.toLowerCase() === 'pending' ? '(Current)' : ''} />
            </MenuItem>
            <MenuItem 
              onClick={() => handleStatusChange('accepted')}
              disabled={
                selectedAppForStatusChange.status?.toLowerCase() === 'accepted' || 
                selectedAppForStatusChange.status?.toLowerCase() === 'approved' ||
                ((stats?.confirmed || 0) >= (event?.capacity || 0) && event?.capacity)
              }
            >
              <ListItemIcon>
                <AcceptIcon fontSize="small" color={selectedAppForStatusChange.status?.toLowerCase() === 'accepted' || selectedAppForStatusChange.status?.toLowerCase() === 'approved' ? 'disabled' : 'success'} />
              </ListItemIcon>
              <ListItemText 
                primary="Set to Accepted" 
                secondary={
                  (selectedAppForStatusChange.status?.toLowerCase() === 'accepted' || selectedAppForStatusChange.status?.toLowerCase() === 'approved') 
                    ? '(Current)' 
                    : ((stats?.confirmed || 0) >= (event?.capacity || 0) && event?.capacity)
                    ? '(Capacity Full)'
                    : ''
                } 
              />
            </MenuItem>
            <MenuItem 
              onClick={() => handleStatusChange('waitlist')}
              disabled={selectedAppForStatusChange.status?.toLowerCase() === 'waitlist'}
            >
              <ListItemIcon>
                <PendingIcon fontSize="small" color={selectedAppForStatusChange.status?.toLowerCase() === 'waitlist' ? 'disabled' : 'info'} />
              </ListItemIcon>
              <ListItemText 
                primary="Set to Waitlist" 
                secondary={selectedAppForStatusChange.status?.toLowerCase() === 'waitlist' ? '(Current)' : 'Add to waitlist'} 
              />
            </MenuItem>
            {selectedAppForStatusChange.status?.toLowerCase() === 'confirmed' && (
              <MenuItem 
                onClick={() => {
                  // Revert confirmed status back to accepted (payment pending)
                  updateStatusMutation.mutate({ 
                    applicationId: selectedAppForStatusChange.application_id, 
                    status: 'accepted' 
                  });
                  handleStatusMenuClose();
                }}
              >
                <ListItemIcon>
                  <ArrowBackIcon fontSize="small" color="action" />
                </ListItemIcon>
                <ListItemText primary="Unconfirm Payment" secondary="Revert to Accepted (Payment Pending)" />
              </MenuItem>
            )}
            <MenuItem 
              onClick={() => handleStatusChange('confirmed')}
              disabled={selectedAppForStatusChange.status?.toLowerCase() === 'confirmed'}
            >
              <ListItemIcon>
                <AcceptIcon fontSize="small" color={selectedAppForStatusChange.status?.toLowerCase() === 'confirmed' ? 'disabled' : 'success'} />
              </ListItemIcon>
              <ListItemText primary="Set to Confirmed" secondary={selectedAppForStatusChange.status?.toLowerCase() === 'confirmed' ? '(Current)' : 'Mark as fully confirmed'} />
            </MenuItem>
            <MenuItem 
              onClick={() => handleStatusChange('rejected')}
              disabled={selectedAppForStatusChange.status?.toLowerCase() === 'rejected'}
            >
              <ListItemIcon>
                <RejectIcon fontSize="small" color={selectedAppForStatusChange.status?.toLowerCase() === 'rejected' ? 'disabled' : 'error'} />
              </ListItemIcon>
              <ListItemText primary="Set to Rejected" secondary={selectedAppForStatusChange.status?.toLowerCase() === 'rejected' ? '(Current)' : ''} />
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Application Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedApplication && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  src={selectedApplication.vendor?.profile_picture_url ? getImageUrl(selectedApplication.vendor.profile_picture_url) : null}
                  sx={{ width: 56, height: 56 }}
                >
                  <StoreIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6">
                    {selectedApplication.vendor?.business_name || 'Unknown Vendor'}
                  </Typography>
                  <Chip
                    label={selectedApplication.status || 'Pending'}
                    color={getStatusColor(selectedApplication.status)}
                    size="small"
                  />
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Stack spacing={3}>
                {/* Vendor Information */}
                <Box>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Vendor Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Business Name
                      </Typography>
                      <Typography variant="body1">
                        {selectedApplication.vendor?.business_name || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Contact
                      </Typography>
                      <Typography variant="body1">
                        {selectedApplication.vendor?.contact_info || 'N/A'}
                      </Typography>
                    </Grid>
                    {selectedApplication.vendor?.bio && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Bio
                        </Typography>
                        <Typography variant="body1">
                          {selectedApplication.vendor.bio}
                        </Typography>
                      </Grid>
                    )}
                    {selectedApplication.vendor?.category_name && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Category
                        </Typography>
                        <Typography variant="body1">
                          {selectedApplication.vendor.category_name}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="outlined"
                      startIcon={<LinkIcon />}
                      onClick={() => {
                        setDetailDialogOpen(false);
                        history.push(`/vendors/${selectedApplication.vendor?.vendor_id}`);
                      }}
                    >
                      View Full Vendor Profile
                    </Button>
                  </Box>
                </Box>

                {/* Application Details */}
                <Box>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Application Details
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Applied On
                      </Typography>
                      <Typography variant="body1">
                        {format(new Date(selectedApplication.created_at), 'PPp')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Status
                      </Typography>
                      <Chip
                        label={selectedApplication.status || 'Pending'}
                        color={getStatusColor(selectedApplication.status)}
                        size="small"
                      />
                    </Grid>
                    {selectedApplication.comments && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Application Comments
                        </Typography>
                        <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                            {(() => {
                              // Remove "Proposal:" and "Price Quote:" labels from comments
                              let cleanedComments = selectedApplication.comments || '';
                              cleanedComments = cleanedComments.replace(/^Proposal:\s*/gim, '');
                              cleanedComments = cleanedComments.replace(/^Price Quote:\s*/gim, '');
                              // Clean up any extra newlines that might result
                              cleanedComments = cleanedComments.replace(/\n{3,}/g, '\n\n');
                              return cleanedComments.trim();
                            })()}
                          </Typography>
                        </Paper>
                      </Grid>
                    )}
                    {selectedApplication.attachment_url && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Attachment
                        </Typography>
                        <Button
                          variant="outlined"
                          startIcon={<DescriptionIcon />}
                          onClick={() => {
                            const raw = selectedApplication.attachment_url;
                            const isAbsolute = /^https?:\/\//i.test(raw);
                            const url = isAbsolute ? raw : getImageUrl(raw);
                            window.open(url, '_blank', 'noopener,noreferrer');
                          }}
                        >
                          View Attachment
                        </Button>
                      </Grid>
                    )}
                    {/* Questionnaire Answers */}
                    {event?.custom_questions && event.custom_questions.length > 0 && 
                     selectedApplication.question_answers && 
                     Object.keys(selectedApplication.question_answers).length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <QuizIcon fontSize="small" />
                          Questionnaire Answers
                        </Typography>
                        <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), mt: 1 }}>
                          <Stack spacing={2}>
                            {event.custom_questions.map((question) => {
                              const answer = selectedApplication.question_answers[question.id];
                              if (!answer || (Array.isArray(answer) && answer.length === 0)) {
                                return null;
                              }
                              return (
                                <Box key={question.id}>
                                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: 'text.primary' }}>
                                    {question.question}
                                    {question.required && <span style={{ color: theme.palette.error.main }}> *</span>}
                                  </Typography>
                                  <Typography 
                                    variant="body1" 
                                    sx={{ 
                                      pl: 2, 
                                      color: 'text.secondary',
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word'
                                    }}
                                  >
                                    {Array.isArray(answer) ? answer.join(', ') : answer}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Stack>
                        </Paper>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions>
              {(selectedApplication.status?.toLowerCase() === 'pending' || selectedApplication.status === 'Pending' || !selectedApplication.status) && (
                <>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<AcceptIcon />}
                    onClick={() => {
                      handleAccept(selectedApplication.application_id);
                      setDetailDialogOpen(false);
                    }}
                    disabled={updateStatusMutation.isLoading}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<RejectIcon />}
                    onClick={() => {
                      handleReject(selectedApplication.application_id);
                      setDetailDialogOpen(false);
                    }}
                    disabled={updateStatusMutation.isLoading}
                  >
                    Reject
                  </Button>
                </>
              )}
              <Button onClick={() => setDetailDialogOpen(false)}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
        </Dialog>

      {/* Questionnaire Answers Dialog */}
      <Dialog
        open={questionnaireDialogOpen}
        onClose={() => setQuestionnaireDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedAppForQuestionnaire && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <QuizIcon color="info" />
                <Typography variant="h6">
                  Questionnaire Answers
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {selectedAppForQuestionnaire.vendor?.business_name || 'Unknown Vendor'}
              </Typography>
            </DialogTitle>
            <DialogContent>
              {event?.custom_questions && event.custom_questions.length > 0 && 
               selectedAppForQuestionnaire.question_answers && 
               Object.keys(selectedAppForQuestionnaire.question_answers).length > 0 ? (
                <Stack spacing={3} sx={{ mt: 1 }}>
                  {event.custom_questions.map((question) => {
                    const answer = selectedAppForQuestionnaire.question_answers[question.id];
                    if (!answer || (Array.isArray(answer) && answer.length === 0)) {
                      return null;
                    }
                    return (
                      <Paper 
                        key={question.id} 
                        sx={{ 
                          p: 2.5, 
                          bgcolor: alpha(theme.palette.primary.main, 0.05),
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                        }}
                      >
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            fontWeight: 600, 
                            mb: 1.5, 
                            color: 'text.primary',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}
                        >
                          {question.question}
                          {question.required && (
                            <Chip 
                              label="Required" 
                              size="small" 
                              color="error" 
                              sx={{ height: 20, fontSize: '0.65rem' }}
                            />
                          )}
                        </Typography>
                        <Box
                          sx={{
                            pl: 2,
                            borderLeft: `3px solid ${theme.palette.primary.main}`,
                          }}
                        >
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              color: 'text.secondary',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              lineHeight: 1.7
                            }}
                          >
                            {Array.isArray(answer) ? answer.join(', ') : answer}
                          </Typography>
                        </Box>
                      </Paper>
                    );
                  })}
                </Stack>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <QuizIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    No questionnaire answers available
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button 
                onClick={() => {
                  setQuestionnaireDialogOpen(false);
                  handleViewDetails(selectedAppForQuestionnaire);
                }}
                variant="outlined"
              >
                View Full Application
              </Button>
              <Button onClick={() => setQuestionnaireDialogOpen(false)} variant="contained">
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Mass Email Dialog */}
      <Dialog 
        open={massEmailDialogOpen} 
        onClose={() => !sendingEmail && setMassEmailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon color="primary" />
            <Typography variant="h6">Send Mass Email to Vendors</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Recipients</InputLabel>
              <Select
                value={massEmailData.recipient_filter}
                label="Recipients"
                onChange={(e) => setMassEmailData({ ...massEmailData, recipient_filter: e.target.value })}
                disabled={sendingEmail}
              >
                <MenuItem value="all">All Vendors ({applications?.length || 0})</MenuItem>
                <MenuItem value="selected">Selected Vendors ({selectedApplications.length})</MenuItem>
                <MenuItem value="accepted">Accepted/Approved Only</MenuItem>
                <MenuItem value="pending">Pending Only</MenuItem>
                <MenuItem value="confirmed">Confirmed Only</MenuItem>
                <MenuItem value="waitlisted">Waitlisted Only</MenuItem>
                <MenuItem value="rejected">Rejected Only</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Email Subject"
              value={massEmailData.subject}
              onChange={(e) => setMassEmailData({ ...massEmailData, subject: e.target.value })}
              disabled={sendingEmail}
              required
              placeholder="e.g., Important Update for {event_title}"
            />

            <TextField
              fullWidth
              multiline
              rows={10}
              label="Email Message"
              value={massEmailData.message}
              onChange={(e) => setMassEmailData({ ...massEmailData, message: e.target.value })}
              disabled={sendingEmail}
              required
              placeholder="Type your message here..."
              helperText="You can use {event_title} and {organizer_name} as placeholders that will be replaced automatically."
            />

            {massEmailData.recipient_filter === 'selected' && selectedApplications.length === 0 && (
              <Alert severity="warning">
                No vendors selected. Please select vendors from the list or choose a different recipient filter.
              </Alert>
            )}

            {massEmailData.recipient_filter !== 'selected' && (
              <Alert severity="info">
                This email will be sent to all vendors matching the selected filter who have verified email addresses.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setMassEmailDialogOpen(false)} 
            disabled={sendingEmail}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={async () => {
              if (!massEmailData.subject || !massEmailData.message) {
                alert('Please fill in both subject and message');
                return;
              }

              if (massEmailData.recipient_filter === 'selected' && selectedApplications.length === 0) {
                alert('Please select vendors to email or choose a different recipient filter');
                return;
              }

              setSendingEmail(true);
              try {
                const emailPayload = {
                  subject: massEmailData.subject,
                  message: massEmailData.message,
                  recipient_filter: massEmailData.recipient_filter,
                  selected_application_ids: massEmailData.recipient_filter === 'selected' 
                    ? selectedApplications 
                    : []
                };

                const result = await sendMassEmail(eventId, emailPayload);
                
                alert(`Email sent successfully! ${result.sent} out of ${result.total_recipients} emails delivered.${result.failed > 0 ? ` ${result.failed} failed.` : ''}`);
                
                setMassEmailDialogOpen(false);
                setMassEmailData({
                  subject: '',
                  message: '',
                  recipient_filter: 'all'
                });
              } catch (error) {
                console.error('Error sending mass email:', error);
                alert(`Failed to send email: ${error.response?.data?.detail || error.message || 'Unknown error'}`);
              } finally {
                setSendingEmail(false);
              }
            }}
            disabled={sendingEmail || !massEmailData.subject || !massEmailData.message}
          >
            {sendingEmail ? 'Sending...' : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>
      </Container>
    </Box>
  );
};

export default EventCRM;

